import React from 'react';
import { Layout, Row, Col, Card, Button, message, Alert } from 'antd';
import { EditFilled, UploadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	siteSurveysRestCrudThunksForBuyer,
	siteSurveysRestCrudThunksForSupplier,
} from '../../thunks/site_surveys_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import TaskListRO from '../task_list/TaskListRO';
import ScheduleWalkthroughModalForm from '../schedule_walkthrough_modal_form/ScheduleWalkthroughModalForm';
import { ExportToCsv } from 'export-to-csv';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import SiteSurveyWalkthroughSection from './SiteSurveyWalkthroughSection';
import { SiteSurveyLocationCard } from './SiteSurveyLocationCard';
import OWFlatfileImporter from '../ow_flatfile/OWFlatfileImporter';
import OWFlatfileButton from '../ow_flatfile/OWFlatfileButton';
import { renderAnswerOptionsCell } from '../ow_flatfile/AnswerOptionsField';
import { renderFollowUpActionsCell } from '../ow_flatfile/FollowUpActionsField';
import {
	getAnswerOptionsForBE,
	getAnswerOptionsForExport,
	getAnswerOptionsSourceValue,
	getFollowUpActionsForBE,
	getFollowUpActionsForExport,
	getSiteSurveyAnswerTypeDisplayName,
	isTaskListEnabled,
	siteSurveyTaskRecordHook,
	SITE_SURVEY_ANSWER_TYPE,
	validateForDuplicateTitlesUnderSameGroup,
} from '../../utils/SiteSurveyUtils';
import WalkthroughHeatmap from '../WalkthroughHeatmap';
import QRCodePrinter from '../PrintableQrCode';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';

const { Content } = Layout;

const csvOptions = {
	fieldSeparator: ',',
	quoteStrings: '"',
	decimalSeparator: '.',
	showLabels: true,
	showTitle: false,
	title: 'Task List',
	useTextFile: false,
	useBom: true,
	headers: [
		'Title',
		'Description',
		'Group Title',
		'Answer Type',
		'Answer Options',
		'Follow-up actions',
	],
};

const FLATFILE_FIELDS = [
	{
		label: 'Title',
		key: 'title',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Description',
		key: 'description',
		type: 'string',
	},
	{
		label: 'Group Title',
		key: 'groupTitle',
		type: 'string',
	},
	{
		label: 'Answer Type',
		key: 'questionType',
		type: 'enum',
		config: {
			options: [
				{
					label: getSiteSurveyAnswerTypeDisplayName(SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE),
					value: SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE,
				},
				{
					label: getSiteSurveyAnswerTypeDisplayName(SITE_SURVEY_ANSWER_TYPE.FREE_TEXT),
					value: SITE_SURVEY_ANSWER_TYPE.FREE_TEXT,
				},
				...(isTaskListEnabled()
					? [
							{
								label: getSiteSurveyAnswerTypeDisplayName(SITE_SURVEY_ANSWER_TYPE.TASK_LIST),
								value: SITE_SURVEY_ANSWER_TYPE.TASK_LIST,
							},
					  ]
					: []),
				{
					label: getSiteSurveyAnswerTypeDisplayName(SITE_SURVEY_ANSWER_TYPE.CHECKLIST),
					value: SITE_SURVEY_ANSWER_TYPE.CHECKLIST,
				},
			],
		},
		constraints: [{ type: 'required' }],
	},
	{
		// One merged column for every answer type. The editor (and the CSV value
		// format) is chosen by the row's answer type; the value is split into the
		// per-type backing keys only when the task list is submitted.
		label: 'Answer Options',
		key: 'answerOptions',
		type: 'string',
		renderCustomCell: renderAnswerOptionsCell,
	},
	{
		// Task-level follow-up actions — applies to every answer type, independent
		// of the per-choice follow-ups configured inside Answer Options.
		label: 'Follow-up actions',
		key: 'followUpActions',
		type: 'string',
		renderCustomCell: renderFollowUpActionsCell,
	},
];

class SiteSurveysDetailPage extends React.Component<any, any> {
	formRefs: any = {
		scheduleWalkthroughForm: null,
	};

	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			scheduleWalkthroughFormVisible: false,
			editTaskListVisible: false,
		};
	}

	componentDidMount() {
		const { getSiteSurvey, match } = this.props;
		const siteSurveyId = match.params.id;
		getSiteSurvey(siteSurveyId).then(() => {
			this.setState({ loading: false });
		});
	}

	handleSchedule = (e) => {
		this.setState({
			scheduleWalkthroughFormVisible: true,
		});
		e.stopPropagation();
		e.preventDefault();
	};

	refreshSiteSurvey = () => {
		const { getSiteSurvey, match } = this.props;
		this.setState({ loading: true });
		const siteSurveyId = match.params.id;
		getSiteSurvey(siteSurveyId).then(() => {
			this.setState({ loading: false });
		});
	};

	handleScheduleWalkthroughCancel = () => {
		this.setState({
			scheduleWalkthroughFormVisible: false,
		});
	};

	saveFormRef = (formName) => (formRef) => {
		this.formRefs[formName] = formRef;
	};

	handleScheduleSuccess = () => {
		message.success('Walkthrough scheduled successfully!');
		this.refreshSiteSurvey();
		this.handleScheduleWalkthroughCancel();
	};

	showEditTaskList = () => this.setState({ editTaskListVisible: true });
	_onEditTaskListComplete = () => this.setState({ editTaskListVisible: false });

	getSourceDataForSiteSurvey = (ss) => {
		return nullSafeGetOrElse('tasks', ss, []).map((task) => ({
			...task,
			answerOptions: getAnswerOptionsSourceValue(task),
		}));
	};

	onDownloadTaskList = () => {
		const { siteSurvey } = this.props;
		const tasks = nullSafeGetOrElse('tasks', siteSurvey, []);
		const formattedTasks = tasks.map((task) => ({
			title: task.title,
			description: task.description || '',
			group: task.groupTitle || '',
			questionType: getSiteSurveyAnswerTypeDisplayName(task.questionType),
			answerOptions: getAnswerOptionsForExport(task),
			followUpActions: getFollowUpActionsForExport(task),
		}));
		const csvExporter = new ExportToCsv({ ...csvOptions, filename: siteSurvey.title });
		csvExporter.generateCsv(formattedTasks);
	};

	onDataLoad = ({ validData }) => {
		return new Promise((resolve, reject) => {
			const { siteSurvey, updateSiteSurvey } = this.props;
			const newSiteSurvey = {
				...siteSurvey,
				areTasksEdited: true,
				tasks: validData.map((task, orderingId) => {
					const {
						answerOptions,
						followUpActions,
						multipleChoicesWithLabel,
						checklistTasks,
						...rest
					} = task;
					return {
						...rest,
						orderingId,
						...getAnswerOptionsForBE(task.questionType, answerOptions),
						...getFollowUpActionsForBE(followUpActions),
					};
				}),
			};
			updateSiteSurvey(newSiteSurvey)
				.then(() => {
					resolve('Updated successfully!');
					this.refreshSiteSurvey();
				})
				.catch(reject);
		});
	};

	recordHook = siteSurveyTaskRecordHook;

	render() {
		const { siteSurvey, match, userType } = this.props;

		const { loading } = this.state;

		const siteSurveyId = match.params.id;

		return loading ? (
			<PageLoadingPlaceholder />
		) : (
			<Content className="siteSurveysDetailDetailsPage">
				{this.state.scheduleWalkthroughFormVisible ? (
					<ScheduleWalkthroughModalForm
						onCancel={this.handleScheduleWalkthroughCancel}
						onSuccess={this.handleScheduleSuccess}
						siteSurveyId={siteSurveyId}
					/>
				) : null}
				<Row style={{ margin: '16px 4px' }} gutter={12}>
					<Col span={16}>
						<WalkthroughHeatmap
							siteSurveyId={match.params.id}
							timeZoneId={nullSafeGet('location.timeZoneId', siteSurvey)}
							siteSurvey={siteSurvey}
						/>
					</Col>
					<Col span={8}>
						{nullSafeGet('siteSurveyUUID', siteSurvey) ? (
							<div className="rowSpacing h-full">
								<QRCodePrinter
									value={siteSurvey.siteSurveyUUID}
									title={'Scan QR code to begin walkthrough'}
									description={`${siteSurvey.title} at ${nullSafeGet('location.name', siteSurvey)}`}
									cardTitle={'Site Survey Label'}
								>
									<Alert
										style={{ marginTop: '32px' }}
										message={
											<div className="text-sm">
												Scan this QR code from the walkthroughs section in the OpenWrench app to
												access the site survey on your mobile device.
											</div>
										}
										showIcon
										type="info"
									/>
								</QRCodePrinter>
							</div>
						) : nullSafeGet('location', siteSurvey) ? (
							<SiteSurveyLocationCard location={siteSurvey.location} />
						) : null}
					</Col>
				</Row>
				<Row style={{ margin: '16px 4px' }} gutter={12}>
					<Col span={24}>
						<div className="rowSpacing">
							<Card
								title="Walkthroughs"
								extra={
									<AccessPermissionChecker name={PERMISSION_NAMES.ADD_WALKTHROUGHS}>
										<Button
											style={{
												position: 'absolute',
												right: 32,
												top: 12.5,
											}}
											type="primary"
											onClick={this.handleSchedule}
										>
											Schedule Walkthrough
										</Button>
									</AccessPermissionChecker>
								}
							>
								<SiteSurveyWalkthroughSection />
							</Card>
						</div>
						<div className="rowSpacing">
							<Card
								title="Tasks"
								extra={
									<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
										<div>
											<Button onClick={this.showEditTaskList}>
												<EditFilled /> Edit Task List
											</Button>
											{this.state.editTaskListVisible ? (
												<OWFlatfileImporter
													dataLoadComplete={this.state.editTaskListVisible}
													type="site_survey"
													entityName="task"
													fields={FLATFILE_FIELDS}
													data={this.getSourceDataForSiteSurvey(siteSurvey)}
													onComplete={this._onEditTaskListComplete}
													onDataLoad={this.onDataLoad}
													recordHook={this.recordHook}
													validateRecordsOnSubmit={validateForDuplicateTitlesUnderSameGroup}
												/>
											) : null}
											<OWFlatfileButton
												type="site_survey_upload"
												entityName="task"
												fields={FLATFILE_FIELDS}
												onDataLoad={this.onDataLoad}
												recordHook={this.recordHook}
												validateRecordsOnSubmit={validateForDuplicateTitlesUnderSameGroup}
												onDownloadSample={this.onDownloadTaskList}
												sampleModalTitle="Replace Task List"
												sampleModalText="You can choose to download the existing list, add new tasks and upload! Do you want to download?"
												sampleButtonText="Download task list"
												showWarningMessage
												renderElement={(openPortal) => (
													<Button
														type="primary"
														style={{ marginLeft: '24px' }}
														onClick={openPortal}
													>
														<UploadOutlined />
														Replace Task List
													</Button>
												)}
											/>
										</div>
									</AccessPermissionChecker>
								}
							>
								<TaskListRO tasks={nullSafeGetOrElse('tasks', siteSurvey, [])} />
							</Card>
						</div>
					</Col>
				</Row>
			</Content>
		);
	}
}

const mapStateToProps = (state) => ({
	siteSurveysFetching: state.site_surveys.fetching,
	siteSurvey: state.site_surveys.detail,
	walkthroughs: state.walkthroughs,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getSiteSurvey: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveysRestCrudThunksForSupplier.readOne(id)
				: siteSurveysRestCrudThunksForBuyer.readOne(id)
		),
	updateSiteSurvey: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveysRestCrudThunksForSupplier.update(id)
				: siteSurveysRestCrudThunksForBuyer.update(id)
		),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(SiteSurveysDetailPage as any) as any)
);
